/**
 * Sysadmin Settings → Notifications (Phase 5 PR-C v1).
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.9 + OQ-1 (single table with
 * is_platform_level flag):
 *   - Channels: platform-scope email / Slack / PagerDuty / webhook /
 *     SMS / in-app destinations for platform alerts.
 *   - Rules: event-pattern routing to those channels.
 *   - Quiet hours: schema-prepped (bA18) but UI surface is v1.1.
 *
 * Two sub-sections under one page (the parent settings rail tab is
 * already 'Notifications'; we use shadcn <Tabs> internally to split
 * channels vs rules). Authoring patterns mirror W1b-24 (tenant
 * Notifications UI) but every API call writes to the platform variant.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Bell,
  Plus,
  Edit2,
  Trash2,
  Mail,
  Slack,
  Phone,
  Webhook,
  MessageSquare,
  Smartphone,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SectionCard } from '@/components/ui/section-card';
import { SettingField } from '@/components/ui/setting-field';
import { StatTile } from '@/components/ui/stat-tile';
import { PageError } from '@/components/ui/page-error';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useSysadminPlatformChannels,
  useCreatePlatformChannel,
  useUpdatePlatformChannel,
  useDeletePlatformChannel,
  useSysadminPlatformRules,
  useCreatePlatformRule,
  useUpdatePlatformRule,
  useDeletePlatformRule,
  type PlatformChannelRow,
  type PlatformRuleRow,
  type ChannelType,
  type Severity,
} from '@/lib/use-sysadmin-settings';

export const Route = createFileRoute('/sysadmin/settings/notifications')({
  component: NotificationsPage,
});

// ─── Channel type metadata ───────────────────────────────────────────────

const CHANNEL_TYPES: ChannelType[] = [
  'email',
  'slack',
  'pagerduty',
  'webhook',
  'sms',
  'in_app',
];

const CHANNEL_LABELS: Record<ChannelType, string> = {
  email: 'Email',
  slack: 'Slack',
  pagerduty: 'PagerDuty',
  webhook: 'Webhook',
  sms: 'SMS',
  in_app: 'In-app',
};

const CHANNEL_ICONS: Record<ChannelType, typeof Bell> = {
  email: Mail,
  slack: Slack,
  pagerduty: Phone,
  webhook: Webhook,
  sms: Smartphone,
  in_app: MessageSquare,
};

const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

// ─── Page ────────────────────────────────────────────────────────────────

function NotificationsPage() {
  const channelsQuery = useSysadminPlatformChannels();
  const rulesQuery = useSysadminPlatformRules();

  const error = channelsQuery.error ?? rulesQuery.error;
  const isLoading = channelsQuery.isLoading || rulesQuery.isLoading;

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load notification routing"
          message={error.message}
          onRetry={() => {
            void channelsQuery.refetch();
            void rulesQuery.refetch();
          }}
          retrying={channelsQuery.isFetching || rulesQuery.isFetching}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Loading platform notifications…
        </div>
      </div>
    );
  }

  const channels = channelsQuery.data?.channels ?? [];
  const rules = rulesQuery.data?.rules ?? [];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Bell className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Platform notifications
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.9</Badge>
              <Badge variant="secondary" className="text-xs">PR-C</Badge>
              <span className="text-xs text-muted-foreground">
                Sysadmin-side routing for platform alerts (GPU OOM, audit gap, license expiry…).
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Channels"
          value={String(channels.length)}
          icon={Bell}
          tone="default"
        />
        <StatTile
          label="Active channels"
          value={String(channels.filter((c) => c.isEnabled).length)}
          icon={Bell}
          tone="success"
        />
        <StatTile
          label="Rules"
          value={String(rules.length)}
          icon={AlertTriangle}
          tone="default"
        />
        <StatTile
          label="Active rules"
          value={String(rules.filter((r) => r.isEnabled).length)}
          icon={AlertTriangle}
          tone={rules.some((r) => r.isEnabled) ? 'success' : 'muted'}
        />
      </div>

      <Tabs defaultValue="channels" className="w-full">
        <TabsList>
          <TabsTrigger value="channels">
            Channels
            <Badge variant="secondary" className="ml-2 text-[10px]">
              {channels.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="rules">
            Rules
            <Badge variant="secondary" className="ml-2 text-[10px]">
              {rules.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="channels" className="mt-4">
          <ChannelsSection channels={channels} />
        </TabsContent>

        <TabsContent value="rules" className="mt-4">
          <RulesSection rules={rules} channels={channels} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Channels section ────────────────────────────────────────────────────

function ChannelsSection({ channels }: { channels: PlatformChannelRow[] }) {
  const [editing, setEditing] = useState<PlatformChannelRow | null>(null);
  const [adding, setAdding] = useState<ChannelType | null>(null);
  const [deleting, setDeleting] = useState<PlatformChannelRow | null>(null);

  const grouped = useMemo(() => {
    const by: Record<ChannelType, PlatformChannelRow[]> = {
      email: [],
      slack: [],
      pagerduty: [],
      webhook: [],
      sms: [],
      in_app: [],
    };
    for (const c of channels) by[c.channelType].push(c);
    return by;
  }, [channels]);

  return (
    <div className="space-y-4">
      {CHANNEL_TYPES.map((type) => {
        const Icon = CHANNEL_ICONS[type];
        const rows = grouped[type];
        return (
          <SectionCard
            key={type}
            title={CHANNEL_LABELS[type]}
            icon={Icon}
            description={`${CHANNEL_LABELS[type]} destinations for platform alerts.`}
            actions={
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAdding(type)}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add channel
              </Button>
            }
          >
            {rows.length === 0 ? (
              <div className="py-3 text-center text-sm text-muted-foreground">
                No platform {CHANNEL_LABELS[type]} channels configured.
              </div>
            ) : (
              <div className="space-y-2">
                {rows.map((row) => (
                  <ChannelRow
                    key={row.id}
                    row={row}
                    onEdit={() => setEditing(row)}
                    onDelete={() => setDeleting(row)}
                  />
                ))}
              </div>
            )}
          </SectionCard>
        );
      })}

      {adding && (
        <ChannelDialog
          mode="add"
          channelType={adding}
          onClose={() => setAdding(null)}
        />
      )}
      {editing && (
        <ChannelDialog
          mode="edit"
          row={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <ChannelDeleteConfirm
          row={deleting}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function ChannelRow({
  row,
  onEdit,
  onDelete,
}: {
  row: PlatformChannelRow;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="text-sm font-medium truncate">{row.name}</div>
        <div className="text-[11px] text-muted-foreground font-mono truncate">
          {row.lastTestAt
            ? `Tested ${new Date(row.lastTestAt).toLocaleString()} · ${row.lastTestOutcome ?? '—'}`
            : 'Never tested'}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant={row.isEnabled ? 'default' : 'secondary'} className="text-[10px]">
          {row.isEnabled ? 'Enabled' : 'Disabled'}
        </Badge>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onEdit}
          title="Edit channel"
        >
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={onDelete}
          title="Delete channel"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

interface ChannelDialogProps {
  mode: 'add' | 'edit';
  channelType?: ChannelType;
  row?: PlatformChannelRow;
  onClose: () => void;
}

function ChannelDialog({ mode, channelType, row, onClose }: ChannelDialogProps) {
  const activeType = row?.channelType ?? channelType!;
  const [name, setName] = useState(row?.name ?? '');
  const [isEnabled, setIsEnabled] = useState(row?.isEnabled ?? true);
  const [configJson, setConfigJson] = useState(
    JSON.stringify(row?.config ?? defaultConfigForType(activeType), null, 2),
  );
  const [configError, setConfigError] = useState<string | null>(null);

  const createMut = useCreatePlatformChannel();
  const updateMut = useUpdatePlatformChannel();
  const pending = createMut.isPending || updateMut.isPending;

  const parseConfig = (): Record<string, unknown> | null => {
    try {
      const parsed = JSON.parse(configJson);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setConfigError('Config must be a JSON object.');
        return null;
      }
      setConfigError(null);
      return parsed as Record<string, unknown>;
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Invalid JSON');
      return null;
    }
  };

  const handleSubmit = () => {
    const config = parseConfig();
    if (!config) return;
    if (mode === 'add') {
      createMut.mutate(
        { name, channelType: activeType, config, isEnabled },
        { onSuccess: onClose },
      );
    } else {
      updateMut.mutate(
        { id: row!.id, name, config, isEnabled },
        { onSuccess: onClose },
      );
    }
  };

  const serverError =
    (createMut.error as Error | null)?.message ??
    (updateMut.error as Error | null)?.message ??
    null;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add'
              ? `Add ${CHANNEL_LABELS[activeType]} channel`
              : `Edit ${row!.name}`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add'
              ? `Platform-scope ${CHANNEL_LABELS[activeType]} destination. Rules can route to this channel.`
              : `Edit name, config, and enabled state. Channel type cannot be changed (delete + recreate to switch).`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <SettingField label="Name" description="Display name; 1-120 chars.">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9 text-sm"
              placeholder="e.g. Platform on-call"
            />
          </SettingField>
          <SettingField
            label="Enabled"
            description="Disabled channels are stored but skipped by the dispatcher."
          >
            <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
          </SettingField>
          <SettingField
            label="Config JSON"
            description={`Provider-specific config for ${CHANNEL_LABELS[activeType]}. Validated server-side.`}
          >
            <Textarea
              value={configJson}
              onChange={(e) => setConfigJson(e.target.value)}
              className="font-mono text-xs min-h-[180px]"
              spellCheck={false}
            />
            {configError && (
              <div className="text-xs text-destructive mt-1">{configError}</div>
            )}
          </SettingField>
          {serverError && (
            <div className="text-xs text-destructive rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
              {serverError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={pending || name.trim() === ''}>
            {pending ? 'Saving…' : mode === 'add' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultConfigForType(t: ChannelType): Record<string, unknown> {
  switch (t) {
    case 'email':
      return { to: [], subjectPrefix: '[ArgusAI]' };
    case 'slack':
      return { webhookUrl: '' };
    case 'pagerduty':
      return { integrationKey: '' };
    case 'webhook':
      return { url: '', method: 'POST' };
    case 'sms':
      return { to: [] };
    case 'in_app':
      return { roles: [], userIds: [] };
  }
}

function ChannelDeleteConfirm({
  row,
  onClose,
}: {
  row: PlatformChannelRow;
  onClose: () => void;
}) {
  const deleteMut = useDeletePlatformChannel();
  return (
    <TypedConfirmDialog
      open={true}
      onOpenChange={(o) => !o && onClose()}
      title={`Delete ${row.name}?`}
      description={
        `Permanently deletes this ${CHANNEL_LABELS[row.channelType]} channel. Rules that reference it will retain the orphan id until you edit them.`
      }
      consequences={[
        'Channel row is hard-deleted from the database',
        'Rules referencing this channel retain the orphan id',
        'A platform_audit_logs row records this deletion with highRiskAction=true',
        row.isEnabled
          ? 'Channel is currently ENABLED — deletion stops platform alerts to this destination immediately'
          : 'Channel is currently disabled (lower risk)',
      ]}
      confirmPhrase={row.name}
      confirmLabel={deleteMut.isPending ? 'Deleting…' : 'Delete channel'}
      onConfirm={() => {
        deleteMut.mutate({ id: row.id }, { onSuccess: onClose });
      }}
    />
  );
}

// ─── Rules section ───────────────────────────────────────────────────────

function RulesSection({
  rules,
  channels,
}: {
  rules: PlatformRuleRow[];
  channels: PlatformChannelRow[];
}) {
  const [editing, setEditing] = useState<PlatformRuleRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<PlatformRuleRow | null>(null);

  return (
    <div className="space-y-4">
      <SectionCard
        title="Routing rules"
        icon={AlertTriangle}
        description="Match event patterns against platform channels."
        actions={
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add rule
          </Button>
        }
      >
        {rules.length === 0 ? (
          <div className="py-3 text-center text-sm text-muted-foreground">
            No platform routing rules configured. Add one to route platform alerts to channels.
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map((row) => (
              <RuleRow
                key={row.id}
                row={row}
                channels={channels}
                onEdit={() => setEditing(row)}
                onDelete={() => setDeleting(row)}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {adding && (
        <RuleDialog
          mode="add"
          channels={channels}
          onClose={() => setAdding(false)}
        />
      )}
      {editing && (
        <RuleDialog
          mode="edit"
          row={editing}
          channels={channels}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <RuleDeleteConfirm row={deleting} onClose={() => setDeleting(null)} />
      )}
    </div>
  );
}

function RuleRow({
  row,
  channels,
  onEdit,
  onDelete,
}: {
  row: PlatformRuleRow;
  channels: PlatformChannelRow[];
  onEdit: () => void;
  onDelete: () => void;
}) {
  const targetNames = row.channelIds
    .map((id) => channels.find((c) => c.id === id)?.name ?? '(missing)')
    .slice(0, 3);
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="text-sm font-medium truncate">{row.name}</div>
        <div className="text-[11px] text-muted-foreground font-mono truncate">
          {row.eventPattern} → {targetNames.join(', ') || '(no channels)'}
          {row.channelIds.length > 3 && ` (+${row.channelIds.length - 3})`}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant={row.isEnabled ? 'default' : 'secondary'} className="text-[10px]">
          {row.isEnabled ? 'Enabled' : 'Disabled'}
        </Badge>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onEdit}
          title="Edit rule"
        >
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={onDelete}
          title="Delete rule"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

interface RuleDialogProps {
  mode: 'add' | 'edit';
  row?: PlatformRuleRow;
  channels: PlatformChannelRow[];
  onClose: () => void;
}

function RuleDialog({ mode, row, channels, onClose }: RuleDialogProps) {
  const [name, setName] = useState(row?.name ?? '');
  const [description, setDescription] = useState(row?.description ?? '');
  const [eventPattern, setEventPattern] = useState(row?.eventPattern ?? '');
  const [severityFilter, setSeverityFilter] = useState<Severity[]>(
    (row?.severityFilter as Severity[]) ?? [],
  );
  const [channelIds, setChannelIds] = useState<string[]>(row?.channelIds ?? []);
  const [isEnabled, setIsEnabled] = useState(row?.isEnabled ?? true);

  const createMut = useCreatePlatformRule();
  const updateMut = useUpdatePlatformRule();
  const pending = createMut.isPending || updateMut.isPending;

  const handleSubmit = () => {
    const body = {
      name,
      description: description.trim() === '' ? undefined : description,
      eventPattern,
      severityFilter,
      channelIds,
      isEnabled,
    };
    if (mode === 'add') {
      createMut.mutate(body, { onSuccess: onClose });
    } else {
      updateMut.mutate(
        { id: row!.id, ...body, description: body.description ?? null },
        { onSuccess: onClose },
      );
    }
  };

  const serverError =
    (createMut.error as Error | null)?.message ??
    (updateMut.error as Error | null)?.message ??
    null;

  const toggleSeverity = (s: Severity) =>
    setSeverityFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  const toggleChannel = (id: string) =>
    setChannelIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add' ? 'Add routing rule' : `Edit ${row!.name}`}
          </DialogTitle>
          <DialogDescription>
            Platform-scope rule that matches incoming events against the
            event pattern + severity filter, then fans out to the selected
            channels.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <SettingField label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9 text-sm"
            />
          </SettingField>
          <SettingField label="Description" description="Free text shown in the UI list.">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="text-sm min-h-[60px]"
            />
          </SettingField>
          <SettingField
            label="Event pattern"
            description="Glob-style pattern matched against incoming event types, e.g. security.* or platform.gpu.oom."
          >
            <Input
              value={eventPattern}
              onChange={(e) => setEventPattern(e.target.value)}
              className="h-9 text-sm font-mono"
              placeholder="e.g. platform.*"
            />
          </SettingField>
          <SettingField
            label="Severity filter"
            description="Empty = match all severities."
          >
            <div className="flex flex-wrap gap-1.5">
              {SEVERITIES.map((s) => (
                <Button
                  key={s}
                  variant={severityFilter.includes(s) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleSeverity(s)}
                  className="h-7 text-[11px]"
                >
                  {s}
                </Button>
              ))}
            </div>
          </SettingField>
          <SettingField
            label="Target channels"
            description="Only platform channels are eligible (defense-in-depth)."
          >
            {channels.length === 0 ? (
              <div className="text-xs text-muted-foreground">
                No platform channels yet. Create one in the Channels tab first.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {channels.map((c) => (
                  <Button
                    key={c.id}
                    variant={channelIds.includes(c.id) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleChannel(c.id)}
                    className="h-7 text-[11px]"
                    disabled={!c.isEnabled}
                    title={!c.isEnabled ? 'Channel is disabled' : undefined}
                  >
                    {c.name}
                  </Button>
                ))}
              </div>
            )}
          </SettingField>
          <SettingField label="Enabled" description="Disabled rules are skipped by the dispatcher.">
            <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
          </SettingField>

          {serverError && (
            <div className="text-xs text-destructive rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
              {serverError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              pending ||
              name.trim() === '' ||
              eventPattern.trim() === ''
            }
          >
            {pending ? 'Saving…' : mode === 'add' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RuleDeleteConfirm({
  row,
  onClose,
}: {
  row: PlatformRuleRow;
  onClose: () => void;
}) {
  const deleteMut = useDeletePlatformRule();
  return (
    <TypedConfirmDialog
      open={true}
      onOpenChange={(o) => !o && onClose()}
      title={`Delete rule "${row.name}"?`}
      description="Permanently removes this routing rule. Platform alerts matching the event pattern will no longer fan out to the configured channels."
      consequences={[
        `Pattern ${row.eventPattern} stops matching this rule immediately`,
        `${row.channelIds.length} channel binding(s) are dropped`,
        'A platform_audit_logs row records this deletion',
      ]}
      confirmPhrase={row.name}
      confirmLabel={deleteMut.isPending ? 'Deleting…' : 'Delete rule'}
      onConfirm={() => {
        deleteMut.mutate({ id: row.id }, { onSuccess: onClose });
      }}
    />
  );
}
